import {
  Component,
  Input,
  ChangeDetectionStrategy,
  HostBinding,
  inject,
  output
} from "@angular/core";
import { TranslatePipe } from "@ngx-translate/core";
import { ThemeService } from "src/service/theme.service";
import { IonIcon } from "@ionic/angular";

@Component({
    selector: "app-no-content",
    templateUrl: "./no-content.component.html",
    styleUrls: ["./no-content.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IonIcon, TranslatePipe]
})
/**
 * Shared empty-state: a neutral badge + a primary message, with an optional
 * hint line and an optional call-to-action button. The look is brand-agnostic
 * (accents derive from --ion-color-* / --ui-*) so every white-label theme
 * inherits it.
 *
 * Backward compatible: call sites that only pass [text] keep working and simply
 * get the refreshed look. Pass [ctaLabel] (+ subscribe to (ctaClick)) to add a
 * primary action, and [compact]="true" for embedded / in-card lists.
 */
export class NoContentComponent {
  private readonly _themeService = inject(ThemeService);

  /** Primary message (rendered as the title). Falls back to the default label. */
  @Input() text: string;

  /** Ionicon name shown inside the badge. Ignored when an SVG src resolves. */
  @Input() icon: string = "file-tray-outline";

  /**
   * Optional path to a custom SVG asset shown inside the badge instead of the
   * ionicon {@link icon} (used on the BIG/Nemo brands). The SVG should use
   * `fill="currentColor"` so it inherits the badge color.
   */
  @Input() iconSrc: string;

  /**
   * Optional Vimar-brand SVG asset. When the app runs in the Vimar theme and
   * this is set, it takes precedence over {@link iconSrc} / {@link icon} so the
   * empty state matches the Vimar menu icon. Ignored on other brands. Centralizes
   * the per-brand choice here, so call sites just declare their section assets.
   */
  @Input() iconVimarSrc: string;

  /**
   * Secondary line under the title. When omitted, a neutral default hint is
   * shown so every empty state stays uniform; pass an empty string to opt out.
   */
  @Input() hint: string;

  /** Optional CTA label. When set (truthy), the action button is rendered. */
  @Input() ctaLabel: string;

  /** Ionicon for the CTA button. */
  @Input() ctaIcon: string = "add";

  /** Compact variant for embedded / in-card lists (smaller, no tall min-height). */
  @Input() compact: boolean = false;

  /** Draws the empty state inside a dashed box */
  @HostBinding("class.no-content--framed")
  @Input()
  framed: boolean = false;

  /** Emitted when the CTA button is tapped. */
  readonly ctaClick = output<void>();

  /**
   * Effective custom-SVG src for the badge: the Vimar asset when running the
   * Vimar theme and one was provided, otherwise the brand-neutral / BIG
   * {@link iconSrc}. Empty/undefined means "fall back to the ionicon {@link icon}".
   *
   * @returns the SVG asset path to render, or a falsy value to use the ionicon
   */
  public get effectiveIconSrc(): string {
    return this._themeService.isVimar && this.iconVimarSrc
      ? this.iconVimarSrc
      : this.iconSrc;
  }

  /** Forwards the CTA tap to the host. */
  public onCtaClick(): void {
    this.ctaClick.emit();
  }
}
